/**
 * AD-042 (2026-06-25) — L3B "Plans & preferences" (the design-path decision +
 * preferences capture). A NEW module for L3B (The Design Path · Sthapana).
 *
 * 6 questions (owner spec): (1) plan/design already in mind · (2) final design
 * decision-maker (name + role) · (3) how approvals run · (4) reference homes /
 * projects / styles · (5) non-negotiables (must-haves) · (6) how strictly Vastu
 * leads vs balance with aesthetics & space.
 *
 * Reuses the spirit of `l1b_plans_preferences` + `decision_maker_map`. Stored as
 * `answers.designPlansPrefs`. Firebase-free + React-free (pure shape mapping).
 */

export const HAVE_PLAN_OPTS = [
  'No — start fresh',
  'Rough idea',
  'Yes — I can share sketches / references',
] as const;

export const APPROVAL_FLOW_OPTS = [
  'Single sign-off',
  'Family consensus',
  'Staged sign-offs',
] as const;

export const VASTU_STRICTNESS_OPTS = [
  'Vastu-strict',
  'Balanced',
  'Vastu where it matters',
] as const;

export interface DesignPlansPrefsInput {
  /* (1) */ havePlan: string;
  /* (2) */ deciderName: string;
  deciderRole: string; // one person / couple / family committee / needs elder or guru sign-off
  /* (3) */ approvalFlow: string;
  /* (4) */ references: string;
  /* (5) */ nonNegotiables: string;
  /* (6) */ vastuStrictness: string;
}

export const DECIDER_ROLE_OPTS = [
  'One person',
  'Couple',
  'Family committee',
  'Needs elder / guru sign-off',
] as const;

export function blankDesignPlansPrefs(): DesignPlansPrefsInput {
  return {
    havePlan: '', deciderName: '', deciderRole: '', approvalFlow: '',
    references: '', nonNegotiables: '', vastuStrictness: '',
  };
}

function t(v: string | undefined): string { return (v || '').trim(); }
function asStr(v: unknown): string { return typeof v === 'string' ? v : v == null ? '' : String(v); }

export function plansPrefsHasContent(p: DesignPlansPrefsInput): boolean {
  return Object.values(p).some((v) => t(v));
}

export function plansPrefsToAnswer(p: DesignPlansPrefsInput): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(p)) { const s = t(v); if (s) out[k] = s; }
  return out;
}

export function plansPrefsFromAnswer(raw: unknown): DesignPlansPrefsInput {
  const d = (raw || {}) as Record<string, unknown>;
  const blank = blankDesignPlansPrefs();
  const out: DesignPlansPrefsInput = { ...blank };
  for (const k of Object.keys(blank) as (keyof DesignPlansPrefsInput)[]) {
    out[k] = asStr(d[k]);
  }
  return out;
}
